
import { localStorageManager } from '../storage/local-storage.js';

/**
 * Set up global event listeners
 */
export function setupEventListeners(ctx) {
    // Window resize handler
    let resizeTimer;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            handleResize(ctx);
        }, 250);
    });

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
        handleKeyboardShortcut(ctx, e);
    });

    // File download handlers (event delegation)
    document.addEventListener('click', (e) => {
        const target = e.target;
        if (!target) return;
        if (target.classList && target.classList.contains('download-btn')) {
            const fileId = target.getAttribute('data-file-id');
            handleFileDownload(ctx, fileId);
        }
    });

    // Mobile menu toggle
    const menuToggle = document.getElementById('mobile-menu-toggle');
    if (menuToggle) {
        menuToggle.addEventListener('click', () => {
            toggleMobileMenu(ctx);
        });
    }

    // Theme toggle
    const themeToggle = document.getElementById('theme-toggle');
    if (themeToggle) {
        themeToggle.addEventListener('click', () => {
            toggleTheme(ctx);
        });
    }
    
    // System theme listener (отслеживание изменений системной темы)
    initSystemThemeListener(ctx);

    // Language switch handler - устанавливает правильный href и переходит по ссылке
    // Сохраняет полностью путь, query параметры и hash (#)
    // Обрабатывает как .language-switch, так и ссылки внутри .language-menu
    // Используем capture phase чтобы перехватить событие ДО Bootstrap
    document.addEventListener('click', (e) => {
        // CRITICAL: Don't block date-range-toggle clicks
        // Check if this is a date-range-toggle click (marked by BootstrapManager)
        if (e._dateRangeToggleClick || e._allowBootstrapDefault) {
            return; // Let Bootstrap handle it
        }
        
        // Also check if click is on date-range-toggle button itself
        const dateRangeToggle = document.getElementById('date-range-toggle');
        if (dateRangeToggle && (e.target === dateRangeToggle || (dateRangeToggle.contains && dateRangeToggle.contains(e.target)))) {
            return; // Don't block date-range-toggle clicks
        }
        
        let target = e.target.closest('.language-switch');
        
        // Если не нашли .language-switch, проверяем ссылки внутри .language-menu
        if (!target) {
            const languageMenu = e.target.closest('.language-menu');
            if (languageMenu) {
                target = e.target.closest('a');
            }
        }
        
        if (target && target.tagName === 'A') {
            e.preventDefault(); // Предотвращаем переход по старому href
            e.stopPropagation(); // Останавливаем всплытие
            e.stopImmediatePropagation(); // Останавливаем другие обработчики
            
            // Пытаемся получить язык из data-lang или из href
            let lang = target.getAttribute('data-lang');
            if (!lang) {
                // Пытаемся извлечь из href, например /en/ -> EN
                const hrefMatch = target.getAttribute('href')?.match(/\/([a-z]{2})\//i);
                if (hrefMatch) {
                    lang = hrefMatch[1].toUpperCase();
                }
            }
            
            if (lang && window.LanguageManager && typeof window.LanguageManager.getLanguageUrl === 'function') {
                const newUrl = window.LanguageManager.getLanguageUrl(lang);
                
                // Отладка (можно убрать потом)
                if (window.debugMode === '1') {
                    console.log('[Language Switch]', {
                        currentUrl: window.location.href,
                        newLang: lang,
                        newUrl: newUrl,
                        pathname: window.location.pathname,
                        search: window.location.search,
                        hash: window.location.hash
                    });
                }
                
                // Всегда устанавливаем правильный href
                target.setAttribute('href', newUrl);
                // Переходим по новой ссылке
                window.location.href = newUrl;
            }
        }
    }, true); // Используем capture phase

    if (debugMode === '1') {
        console.log('[events] Event listeners set up');
    }
}

export function handleKeyboardShortcut(ctx, event) {
    // Ctrl/Cmd + K - Focus search
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        const searchInput = document.getElementById('search-input');
        if (searchInput) searchInput.focus();
    }

    // Ctrl/Cmd + / - Show shortcuts
    if ((event.ctrlKey || event.metaKey) && event.key === '/') {
        event.preventDefault();
        ctx.showKeyboardShortcuts();
    }

    // Escape - Close only the topmost Bootstrap modal if present, else clear search / close custom modals
    if (event.key === 'Escape') {
        try {
            var $modals = window.jQuery ? window.jQuery('.modal.show') : null;
            if ($modals && $modals.length) {
                // Close the last (topmost) shown modal only
                var el = $modals[$modals.length - 1];
                try { (window.bootstrap && window.bootstrap.Modal.getInstance(el) || window.bootstrap.Modal.getOrCreateInstance(el)).hide(); } catch(_) {}
                return;
            }
        } catch(_) {}

        if (document.activeElement && document.activeElement.id === 'search-input') {
            if (window.searchUI && typeof window.searchUI.clearSearch === 'function') {
                window.searchUI.clearSearch();
            }
        }
        // Fallback: legacy custom modals
        ctx.closeAllModals();
    }
}

export async function handleFileDownload(ctx, fileId) {
    try {
        const file = (window.fileListManager && typeof window.fileListManager.getFileById === 'function')
            ? window.fileListManager.getFileById(fileId)
            : null;
        if (!file) {
            throw new Error('File not found');
        }

        // Track download
        ctx.trackEvent('file_download', {
            fileId: fileId,
            fileName: file.name,
            format: file.format
        });

        // Initiate download
        window.location.href = `/api/download/${fileId}`; // CRITICAL: Direct API URL
    } catch (error) {
        if (debugMode === '1') {
            console.error('Download error:', error);
        }
        ctx.showError('Не удалось скачать файл');
    }
}

export function handleResize(ctx) {
    const width = window.innerWidth;

    // Update mobile state
    if (width < 768) {
        document.body.classList.add('mobile');
    } else {
        document.body.classList.remove('mobile');
    }

    // Notify modules that need to know about resize
    try { if (window.fileListManager && window.fileListManager.handleResize) window.fileListManager.handleResize(); } catch(_) {}
    try { if (window.searchUI && window.searchUI.handleResize) window.searchUI.handleResize(); } catch(_) {}
}

export function toggleMobileMenu(ctx) {
    document.body.classList.toggle('mobile-menu-open');
}

export function toggleTheme(ctx) {
    const htmlEl = document.documentElement;
    const isCurrentlyDark = htmlEl.getAttribute('data-theme') === 'dark';
    
    if (isCurrentlyDark) {
        htmlEl.removeAttribute('data-theme'); // Light mode (дефолт)
        try { localStorageManager.set('theme', 'light'); } catch(_) {}
        broadcastThemeChange(ctx, 'light');
    } else {
        htmlEl.setAttribute('data-theme', 'dark');
        try { localStorageManager.set('theme', 'dark'); } catch(_) {}
        broadcastThemeChange(ctx, 'dark');
    }
}

export function broadcastThemeChange(ctx, theme) {
    const modules = ctx && ctx.modules ? Object.values(ctx.modules) : [];
    modules.forEach((module) => {
        try {
            if (module && typeof module.onThemeChange === 'function') {
                module.onThemeChange(theme);
            }
        } catch(_) { /* noop */ }
    });
}

/**
 * Инициализирует listener для отслеживания изменений системной темы
 * Реагирует на изменения в Chrome DevTools (Rendering → Emulate prefers-color-scheme)
 * 
 * КРИТИЧНО: При загрузке страницы НЕ следуем системной теме - только сохраненному предпочтению пользователя
 * или light mode по умолчанию. Слушатель системной темы работает только для динамических изменений.
 */
function initSystemThemeListener(ctx) {
    if (!window.matchMedia) return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    function handleThemeChange(e) {
        const htmlEl = document.documentElement;
        
        // Проверяем, есть ли сохраненное предпочтение пользователя
        let savedTheme = null;
        try {
            savedTheme = localStorageManager?.get('theme');
        } catch(_) {}
        
        // Если пользователь не сохранил предпочтение, НЕ следуем системной теме при загрузке
        // Слушатель работает только для динамических изменений после загрузки страницы
        // Но мы не применяем системную тему автоматически - только light mode по умолчанию
        if (!savedTheme) {
            // Не применяем системную тему - оставляем light mode по умолчанию
            // Это предотвращает автоматическое включение темной темы при загрузке
            return;
        }
    }
    
    // Слушаем изменения (только для динамических изменений после загрузки)
    if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleThemeChange);
    } else {
        // Fallback для старых браузеров
        mediaQuery.addListener(handleThemeChange);
    }
    
    // Инициализация при загрузке
    let savedTheme = null;
    try {
        savedTheme = localStorageManager?.get('theme');
    } catch(_) {}
    
    // Применяем ТОЛЬКО сохраненную тему пользователя или light mode по умолчанию
    // НЕ следуем системной теме при загрузке страницы
    const htmlEl = document.documentElement;
    if (savedTheme === 'dark') {
        htmlEl.setAttribute('data-theme', 'dark');
        broadcastThemeChange(ctx, 'dark');
    } else {
        // По умолчанию light mode (не следуем системной теме)
        htmlEl.removeAttribute('data-theme');
        broadcastThemeChange(ctx, 'light');
    }
}


